import { useTranslation } from 'react-i18next';
import { OverviewFilters } from '../../types/overview';

// UI Components
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { DatePicker } from '../ui/date-picker';

interface OverviewFiltersProps {
  filters: OverviewFilters;
  onChange: (filters: OverviewFilters) => void;
  isLoading: boolean;
}

export function OverviewFiltersComponent({ 
  filters, 
  onChange, 
  isLoading 
}: OverviewFiltersProps) {
  const { t } = useTranslation();
  
  // Fonction pour calculer les dates selon la période
  const calculateDatesForPeriod = (period: string) => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date;
    
    switch (period) {
      case 'yesterday':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 1);
        endDate = new Date(startDate);
        break;
      case 'last7days':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        endDate = new Date(now);
        endDate.setDate(now.getDate() - 1);
        break;
      case 'last30days':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 30);
        endDate = new Date(now);
        endDate.setDate(now.getDate() - 1);
        break;
      case 'lastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 1);
        endDate = new Date(startDate);
    }
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };
  
  // Calculer les dates actuelles si elles ne sont pas définies
  const currentDates = calculateDatesForPeriod(filters.period);
  const displayStartDate = filters.customStartDate || currentDates.startDate;
  const displayEndDate = filters.customEndDate || currentDates.endDate;

  const handleSolutionChange = (solution: string) => {
    onChange({
      ...filters,
      solution: solution as 'ssp' | 'adblock' | 'noconsent'
    });
  };

  const handlePeriodChange = (period: string) => {
    const calculatedDates = calculateDatesForPeriod(period);
    onChange({
      ...filters,
      period: period as 'yesterday' | 'last7days' | 'last30days' | 'lastMonth' | 'custom',
      customStartDate: calculatedDates.startDate,
      customEndDate: calculatedDates.endDate
    });
  };

  const handleCustomStartDateChange = (date: Date | undefined) => {
    if (date) {
      onChange({
        ...filters,
        customStartDate: date.toISOString().split('T')[0],
        period: 'custom'
      });
    }
  };

  const handleCustomEndDateChange = (date: Date | undefined) => {
    if (date) {
      onChange({
        ...filters,
        customEndDate: date.toISOString().split('T')[0],
        period: 'custom'
      });
    }
  };

  const handleVariationToggle = (showVariation: boolean) => {
    onChange({
      ...filters,
      showVariation
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Sélection de solution */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Solution</Label>
          <Tabs value={filters.solution} onValueChange={handleSolutionChange}>
            <TabsList className="grid w-full grid-cols-3 h-10">
              <TabsTrigger 
                value="ssp" 
                disabled={isLoading}
                className="text-sm px-4"
              >
                {t('overview.solutions.ssp')}
              </TabsTrigger>
              <TabsTrigger 
                value="adblock" 
                disabled={isLoading}
                className="text-sm px-4"
              >
                {t('overview.solutions.adblock')}
              </TabsTrigger>
              <TabsTrigger 
                value="noconsent" 
                disabled={isLoading}
                className="text-sm px-4"
              >
                {t('overview.solutions.noconsent')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Sélection de période */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Period</Label>
          <Tabs value={filters.period} onValueChange={handlePeriodChange}>
            <TabsList className="grid w-full grid-cols-5 h-10">
              <TabsTrigger 
                value="yesterday" 
                disabled={isLoading}
                className="text-xs px-1"
              >
                {t('overview.periods.yesterday')}
              </TabsTrigger>
              <TabsTrigger 
                value="last7days" 
                disabled={isLoading}
                className="text-xs px-1"
              >
                {t('overview.periods.last7days')}
              </TabsTrigger>
              <TabsTrigger 
                value="last30days" 
                disabled={isLoading}
                className="text-xs px-1"
              >
                {t('overview.periods.last30days')}
              </TabsTrigger>
              <TabsTrigger 
                value="lastMonth" 
                disabled={isLoading}
                className="text-xs px-1"
              >
                {t('overview.periods.lastMonth')}
              </TabsTrigger>
              <TabsTrigger 
                value="custom" 
                disabled={isLoading}
                className="text-xs px-1"
              >
                {t('overview.periods.custom', 'Custom')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Toggle pour afficher la variation */}
        <div className="space-y-2">
          <Label className="text-sm font-medium opacity-0">Toggle</Label>
          <div className="flex items-center justify-start space-x-3 h-10 px-4 border rounded-lg bg-muted/50">
            <Switch
              id="show-variation"
              checked={filters.showVariation}
              onCheckedChange={handleVariationToggle}
              disabled={isLoading}
            />
            <Label htmlFor="show-variation" className="text-sm font-medium">
              {t('overview.showVariation')}
            </Label>
          </div>
        </div>
      </div>

      {/* Dates personnalisées - Toujours affichées */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-muted/20">
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {t('common.startDate', 'Start Date')}
          </Label>
          <DatePicker
            date={displayStartDate ? new Date(displayStartDate) : undefined}
            onSelect={handleCustomStartDateChange}
            placeholder={t('common.selectStartDate', 'Select start date')}
            disabled={isLoading || filters.period !== 'custom'}
            className="w-full"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {t('common.endDate', 'End Date')}
          </Label>
          <DatePicker
            date={displayEndDate ? new Date(displayEndDate) : undefined}
            onSelect={handleCustomEndDateChange}
            placeholder={t('common.selectEndDate', 'Select end date')}
            disabled={isLoading || filters.period !== 'custom'}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
}